/**
 * Compact encounters/visits timeline for the patient sidebar "Visits" tab.
 *
 * Uses the same sectioned content-preview primitives as the full patient
 * timeline so Forms / Notes / Files / Invoices / Vitals render with actual
 * content (not just counts). See ../pages/patient/encounters/timeline-previews.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavIcon } from './shared';
import { usePowerSyncQuery } from '../adapters/powersync';
import {
  qEncountersByPatient,
  qEncounterSummaryCounts,
  qEncounterNotesPreviews,
  qEncounterFormsPreviews,
  qEncounterInvoicesPreviews,
  qEncounterInvoiceItems,
  qEncounterFilesDetails,
  qEncounterVitalsDetails,
  qPatientVisualCharts,
  qOdontogramChartsByPatient,
  type PatientEncounterRow,
  type EncounterSummaryCountRow,
  type EncounterNotePreviewRow,
  type EncounterFormPreviewRow,
  type EncounterInvoicePreviewRow,
  type EncounterInvoiceItemRow,
  type EncounterFileDetailRow,
  type EncounterVitalsDetailRow,
  type EncounterVisualChartRow,
  type OdontogramChartRow,
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  type PatientWorkspaceSettingsRow,
} from '@helios/data-access';
import {
  PREVIEW_LIMIT,
  SectionLabel,
  MoreLink,
  EmbeddedFormRow,
  FileThumbnail,
  DocumentFileRow,
  VitalChip,
  InvoicePreviewRow,
  NotePreview,
  DrawingPreviewRow,
  ToothChartPreviewRow,
  isImageFile,
} from '../pages/patient/encounters/timeline-previews';

// ─── Helpers ───

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation',
  procedure: 'Procedure',
  follow_up: 'Follow-up',
  triage: 'Triage',
};

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  consultation: { bg: '#dbeafe', color: '#2563eb' },
  procedure: { bg: '#fef3c7', color: '#d97706' },
  follow_up: { bg: '#f3e8ff', color: '#7c3aed' },
  triage: { bg: '#fecaca', color: '#dc2626' },
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  open: { bg: '#dcfce7', color: '#16a34a' },
  closed: { bg: '#f3f4f6', color: '#6b7280' },
};

const VITALS_CHIP_LIMIT = 3;

// ─── Component ───

export function SidebarVisitsPanel({
  patientId,
  selectedEncounterId,
  onSelectEncounter,
  onOpenFormDrilldown,
  onOpenInvoiceDrilldown,
  onOpenFileDrilldown,
}: {
  patientId: string;
  selectedEncounterId?: string;
  onSelectEncounter: (id: string) => void;
  // PRD.180 drill-in callbacks. When provided, clicking the matching
  // timeline artifact opens a right drawer instead of navigating. When
  // omitted, fall back to full-page navigation.
  onOpenFormDrilldown?: (formInstanceId: string, encounterId: string) => void;
  onOpenInvoiceDrilldown?: (invoiceId: string, encounterId: string) => void;
  onOpenFileDrilldown?: (fileId: string, encounterId: string) => void;
}) {
  const navigate = useNavigate();

  // ── Queries ──
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );
  const { data: summaryCounts = [] } = usePowerSyncQuery<EncounterSummaryCountRow>(
    qEncounterSummaryCounts(patientId),
  );
  const { data: notesPreviews = [] } = usePowerSyncQuery<EncounterNotePreviewRow>(
    qEncounterNotesPreviews(patientId),
  );
  const { data: formsPreviews = [] } = usePowerSyncQuery<EncounterFormPreviewRow>(
    qEncounterFormsPreviews(patientId),
  );
  const { data: invoicesPreviews = [] } = usePowerSyncQuery<EncounterInvoicePreviewRow>(
    qEncounterInvoicesPreviews(patientId),
  );
  const { data: invoiceItems = [] } = usePowerSyncQuery<EncounterInvoiceItemRow>(
    qEncounterInvoiceItems(patientId),
  );
  const { data: filesDetails = [] } = usePowerSyncQuery<EncounterFileDetailRow>(
    qEncounterFilesDetails(patientId),
  );
  const { data: vitalsDetails = [] } = usePowerSyncQuery<EncounterVitalsDetailRow>(
    qEncounterVitalsDetails(patientId),
  );
  const { data: visualCharts = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qPatientVisualCharts(patientId),
  );
  const { data: odontogramCharts = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByPatient(patientId),
  );
  const { data: workspaceSettingsRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  // Treatment Record mode (PRD.182 §5.5): clicking a timeline card must
  // not navigate to the user-facing Visit Note page.
  const isTreatmentRecordMode =
    workspaceSettingsRows.length > 0 &&
    mapPatientWorkspaceSettingsToUI(workspaceSettingsRows[0]).workspaceMode ===
      'treatment_record';

  const sorted = useMemo(
    () =>
      [...encounterRows].sort((a, b) => {
        const da = a.started_at ?? a.created_at ?? '';
        const db = b.started_at ?? b.created_at ?? '';
        return new Date(db).getTime() - new Date(da).getTime();
      }),
    [encounterRows],
  );

  // ── Derived maps ──
  const countsMap = useMemo(() => {
    const map: Record<string, EncounterSummaryCountRow> = {};
    for (const r of summaryCounts) map[r.encounter_id] = r;
    return map;
  }, [summaryCounts]);

  const notesMap = useMemo(() => {
    const map: Record<string, EncounterNotePreviewRow[]> = {};
    for (const r of notesPreviews) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [notesPreviews]);

  const formsMap = useMemo(() => {
    const map: Record<string, EncounterFormPreviewRow[]> = {};
    for (const r of formsPreviews) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [formsPreviews]);

  const invoicesMap = useMemo(() => {
    const map: Record<string, EncounterInvoicePreviewRow[]> = {};
    for (const r of invoicesPreviews) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [invoicesPreviews]);

  const invoiceItemsByInvoice = useMemo(() => {
    const map: Record<string, EncounterInvoiceItemRow[]> = {};
    for (const r of invoiceItems) {
      if (!map[r.invoice_id]) map[r.invoice_id] = [];
      map[r.invoice_id].push(r);
    }
    return map;
  }, [invoiceItems]);

  const filesMap = useMemo(() => {
    const map: Record<string, EncounterFileDetailRow[]> = {};
    for (const r of filesDetails) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [filesDetails]);

  const vitalsMap = useMemo(() => {
    const map: Record<string, EncounterVitalsDetailRow[]> = {};
    for (const r of vitalsDetails) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [vitalsDetails]);

  const chartsByEncounter = useMemo(() => {
    const map: Record<string, EncounterVisualChartRow[]> = {};
    for (const c of visualCharts) {
      if (!map[c.encounter_id]) map[c.encounter_id] = [];
      map[c.encounter_id].push(c);
    }
    return map;
  }, [visualCharts]);

  const toothChartsByEncounter = useMemo(() => {
    const map: Record<string, OdontogramChartRow[]> = {};
    for (const c of odontogramCharts) {
      if (!c.encounter_id) continue;
      if (!map[c.encounter_id]) map[c.encounter_id] = [];
      map[c.encounter_id].push(c);
    }
    return map;
  }, [odontogramCharts]);

  const filesById = useMemo(() => {
    const map = new Map<string, { url: string | null }>();
    for (const f of filesDetails) map.set(f.id, { url: f.url });
    return map;
  }, [filesDetails]);

  if (sorted.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-6)',
          textAlign: 'center',
          color: 'var(--text-placeholder)',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        No visits yet
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', paddingLeft: 20 }}>
      {/* Vertical timeline line */}
      <div
        style={{
          position: 'absolute',
          left: 5,
          top: 8,
          bottom: 8,
          width: 2,
          background: 'var(--border)',
          borderRadius: 1,
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {sorted.map((enc) => {
          const isSelected = selectedEncounterId === enc.id;
          const isOpen = enc.status === 'open';
          const typeLabel = TYPE_LABELS[enc.type] ?? enc.type ?? 'Visit';
          const typeColor = TYPE_COLORS[enc.type] ?? { bg: '#f3f4f6', color: '#6b7280' };
          const statusColor = STATUS_COLORS[enc.status] ?? STATUS_COLORS.closed;

          const forms = formsMap[enc.id] ?? [];
          const notes = notesMap[enc.id] ?? [];
          const files = filesMap[enc.id] ?? [];
          const invoices = invoicesMap[enc.id] ?? [];
          const vitals = vitalsMap[enc.id] ?? [];
          const charts = chartsByEncounter[enc.id] ?? [];
          const toothCharts = toothChartsByEncounter[enc.id] ?? [];
          const counts = countsMap[enc.id];
          const hasAny =
            forms.length > 0 ||
            notes.length > 0 ||
            files.length > 0 ||
            invoices.length > 0 ||
            vitals.length > 0 ||
            charts.length > 0 ||
            toothCharts.length > 0;

          const imageFiles = files.filter(isImageFile);
          const docFiles = files.filter((f) => !isImageFile(f));
          const imgVisible = imageFiles.slice(0, 3);
          const docVisible = docFiles.slice(0, PREVIEW_LIMIT);
          const filesHidden =
            imageFiles.length - imgVisible.length + (docFiles.length - docVisible.length);

          const vitalsVisible = vitals.slice(0, VITALS_CHIP_LIMIT);
          const vitalsHidden = vitals.length - vitalsVisible.length;

          const openEncounter = () => onSelectEncounter(enc.id);
          const filesTab = `/patient/${patientId}/encounters/${enc.id}?tab=files`;

          return (
            <div key={enc.id} style={{ position: 'relative' }}>
              {/* Timeline dot */}
              <div
                style={{
                  position: 'absolute',
                  left: -18,
                  top: 14,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: isOpen ? 'var(--color-primary, #2563eb)' : 'var(--text-placeholder)',
                  border: '2px solid var(--bg-surface)',
                  boxShadow: isOpen ? '0 0 0 3px rgba(37, 99, 235, 0.12)' : undefined,
                  zIndex: 1,
                }}
              />
              <div
                role={isTreatmentRecordMode ? undefined : 'button'}
                tabIndex={isTreatmentRecordMode ? undefined : 0}
                onClick={isTreatmentRecordMode ? undefined : openEncounter}
                onKeyDown={
                  isTreatmentRecordMode
                    ? undefined
                    : (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openEncounter();
                        }
                      }
                }
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  padding: 'var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  border: isSelected
                    ? '2px solid var(--color-primary, #92400e)'
                    : `1px solid ${isOpen ? '#fde68a' : 'var(--border)'}`,
                  background: isSelected
                    ? 'var(--color-primary-subtle, #fffbeb)'
                    : 'var(--bg-surface)',
                  cursor: isTreatmentRecordMode ? 'default' : 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  outline: 'none',
                }}
              >
                {/* Currently viewing indicator */}
                {isSelected && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 9,
                      fontWeight: 600,
                      color: 'var(--color-primary, #2563eb)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 2,
                    }}
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    This Visit Note
                  </div>
                )}

                {/* Row 1: Type + Status chips */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px 7px',
                      borderRadius: 'var(--radius-full)',
                      background: typeColor.bg,
                      color: typeColor.color,
                      fontWeight: 600,
                    }}
                  >
                    {typeLabel}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px 7px',
                      borderRadius: 'var(--radius-full)',
                      background: statusColor.bg,
                      color: statusColor.color,
                      fontWeight: 600,
                    }}
                  >
                    {isOpen ? 'Open' : 'Closed'}
                  </span>
                </div>

                {/* Row 2: Date/Time */}
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <NavIcon name="calendar" />
                  {fmtDate(enc.started_at)} · {fmtTime(enc.started_at)}
                </div>

                {/* Row 3: Provider */}
                {enc.provider_name && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <NavIcon name="user" />
                    {enc.provider_name}
                  </div>
                )}

                {/* Content preview sections */}
                {hasAny && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      marginTop: 2,
                    }}
                  >
                    {/* Vitals chips */}
                    {vitals.length > 0 && (
                      <>
                        <SectionLabel label="Vitals" count={vitals.length} />
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 4,
                            alignItems: 'center',
                          }}
                        >
                          {vitalsVisible.map((v, vi) => (
                            <VitalChip key={vi} vital={v} />
                          ))}
                          {vitalsHidden > 0 && (
                            <span
                              style={{
                                fontSize: 10,
                                color: 'var(--text-placeholder)',
                                fontWeight: 'var(--font-weight-medium)',
                              }}
                            >
                              +{vitalsHidden} more
                            </span>
                          )}
                        </div>
                      </>
                    )}

                    {/* Forms — embedded mini list */}
                    {forms.length > 0 && (
                      <>
                        <SectionLabel label="Forms" count={forms.length} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {forms.slice(0, PREVIEW_LIMIT).map((f) => (
                            <EmbeddedFormRow
                              key={f.id}
                              form={f}
                              onOpen={() => {
                                if (onOpenFormDrilldown) {
                                  onOpenFormDrilldown(f.id, enc.id);
                                } else {
                                  navigate(
                                    `/patient/${patientId}/encounters/${enc.id}/forms/${f.id}`,
                                  );
                                }
                              }}
                            />
                          ))}
                          {forms.length > PREVIEW_LIMIT && (
                            <MoreLink count={forms.length - PREVIEW_LIMIT} singular="form" />
                          )}
                        </div>
                      </>
                    )}

                    {/* Notes — full title + multi-line body so the sidebar
                        summary matches the Notes tab content. */}
                    {notes.length > 0 && (
                      <>
                        <SectionLabel label="Notes" count={notes.length} />
                        {notes.slice(0, PREVIEW_LIMIT).map((n, ni) => (
                          <NotePreview key={n.created_at ?? `${ni}`} note={n} />
                        ))}
                        {notes.length > PREVIEW_LIMIT && (
                          <MoreLink count={notes.length - PREVIEW_LIMIT} singular="note" />
                        )}
                      </>
                    )}

                    {/* Files — adaptive: thumbnails for images, rows for docs */}
                    {files.length > 0 && (
                      <>
                        <SectionLabel label="Files" count={files.length} />
                        {imgVisible.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {imgVisible.map((f) => (
                              <FileThumbnail
                                key={f.id}
                                file={f}
                                onOpen={() => {
                                  if (onOpenFileDrilldown) {
                                    onOpenFileDrilldown(f.id, enc.id);
                                  } else if (f.url) {
                                    window.open(f.url, '_blank', 'noopener');
                                  } else {
                                    navigate(filesTab);
                                  }
                                }}
                              />
                            ))}
                          </div>
                        )}
                        {docVisible.map((f) => (
                          <DocumentFileRow
                            key={f.id}
                            file={f}
                            onOpen={() => {
                              if (onOpenFileDrilldown) {
                                onOpenFileDrilldown(f.id, enc.id);
                              } else {
                                navigate(filesTab);
                              }
                            }}
                          />
                        ))}
                        {filesHidden > 0 && <MoreLink count={filesHidden} singular="file" />}
                      </>
                    )}

                    {/* Saved Chartings — tooth charts + drawings + before/after */}
                    {(charts.length > 0 || toothCharts.length > 0) && (
                      <>
                        {(() => {
                          const limit = 3;
                          const total = charts.length + toothCharts.length;
                          const toothVisible = toothCharts.slice(0, limit);
                          const remaining = Math.max(0, limit - toothVisible.length);
                          const visualVisible = charts.slice(0, remaining);
                          const hidden = total - toothVisible.length - visualVisible.length;
                          return (
                            <>
                              <SectionLabel label="Saved Chartings" count={total} />
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {toothVisible.map((tc) => (
                                  <ToothChartPreviewRow
                                    key={tc.id}
                                    chart={tc}
                                    onOpen={() =>
                                      navigate(
                                        `/patient/${patientId}/encounters/${enc.id}?tab=charting`,
                                      )
                                    }
                                  />
                                ))}
                                {visualVisible.map((c) => (
                                  <DrawingPreviewRow
                                    key={c.id}
                                    chart={c}
                                    filesById={filesById}
                                    onOpen={() =>
                                      navigate(
                                        `/patient/${patientId}/encounters/${enc.id}?tab=charting`,
                                      )
                                    }
                                  />
                                ))}
                              </div>
                              {hidden > 0 && <MoreLink count={hidden} singular="charting" />}
                            </>
                          );
                        })()}
                      </>
                    )}

                    {/* Invoices */}
                    {invoices.length > 0 && (
                      <>
                        <SectionLabel label="Invoices" count={invoices.length} />
                        {invoices.slice(0, PREVIEW_LIMIT).map((inv) => (
                          <InvoicePreviewRow
                            key={inv.id}
                            invoice={inv}
                            items={invoiceItemsByInvoice[inv.id] ?? []}
                            onOpen={() => {
                              if (onOpenInvoiceDrilldown) {
                                onOpenInvoiceDrilldown(inv.id, enc.id);
                              } else {
                                navigate(
                                  `/patient/${patientId}/encounters/${enc.id}?tab=invoices&invoiceId=${inv.id}`,
                                );
                              }
                            }}
                          />
                        ))}
                        {invoices.length > PREVIEW_LIMIT && (
                          <MoreLink count={invoices.length - PREVIEW_LIMIT} singular="invoice" />
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Empty state */}
                {!hasAny && counts && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      fontStyle: 'italic',
                      marginTop: 2,
                    }}
                  >
                    No activity recorded
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
